// Screen 1, Performance (CR-002 CR2-030; J-060), at /groups?year&aco&sort&filter&view. `sort` is
// `{measureKey}:{asc|desc}` and `filter` `{measureKey|any}:{below|above}:{value}`, so a link keeps them.
// `view=groups` shows the same screen by performance group (J-202); its switch needs `acoQuality.read`.
// The configured texts, columns and measure order are read here, where features are composed (C6).
// J-209 (the owner's inbox file 06, PR D): "All" shows the ACOs with results for the year (item 2); a
// read that fails, a refusal and an invalid ui document each say so (item 7); a malformed sort or
// filter in the address is dropped rather than failing the page (item 8); an ACO code not in scope is
// "not found" (item 11); a practice role reads why the screen is not theirs (item 16).
// J-238 (group access): a group-scoped user sees only the group view, of its granted groups; the
// practice view reads the whole ACO, which the server refuses it.
// J-277 (QB3 items 8 and 8b; Louis, 2026-10-17: "On performance screen screen, It opens on Performance
// group, and the group choice comes before the practice choice"): "View by: Performance group |
// Practice", the group view the default; `view=practices` names the practice view and `view=groups`
// still opens the group view (`lib/screen-view.ts`). The practice view keeps a Performance group filter
// (`group`).
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { type MatrixFilter, type MatrixSort, uiDocumentSchema } from "@vbltc/schema";

import { metadataQueryOptions } from "@/features/aco-shell/api/metadata";
import { uiConfigQueryOptions } from "@/features/aco-shell/api/ui-config";
import { AcoNotFound } from "@/features/aco-shell/components/aco-not-found";
import { ShellStatusMessage } from "@/features/aco-shell/components/shell-status-message";
import {
  ALL_ACOS,
  acosWithData,
  defaultAcoCode,
  resolveYear,
} from "@/features/aco-shell/default-aco";
import { shellStatus } from "@/features/aco-shell/shell-status";
import { GroupPerformanceScreen } from "@/features/aco-groups/components/group-performance-screen";
import { PerformanceScreen } from "@/features/aco-groups/components/performance-screen";
import { ViewSwitch } from "@/features/aco-groups/components/view-switch";
import { meAccessQueryOptions, readMyAccess } from "@/features/auth/api/me-access";
import { usePermission } from "@/features/auth/use-permission";
import { grantedAcoCodes, isGroupScoped } from "@/lib/group-access";
import { groupsSearchSchema } from "@/lib/route-search";
import { requestedView, viewKey } from "@/lib/screen-view";

function parseSort(text: string | undefined): MatrixSort | undefined {
  if (text === undefined) return undefined;
  const [key, dir] = text.split(":");
  return { measureKey: Number(key), direction: dir === "asc" ? "ascending" : "descending" };
}

function parseFilter(text: string | undefined): MatrixFilter | undefined {
  if (text === undefined) return undefined;
  const [key, condition, value] = text.split(":");
  return {
    measureKey: key === "any" ? null : Number(key),
    condition: condition === "above" ? "above" : "below",
    value: Number(value),
  };
}

export const Route = createFileRoute("/_authenticated/groups/")({
  validateSearch: groupsSearchSchema,
  beforeLoad: async ({ context, search }) => {
    if (requestedView(search) === "groups") return;
    if (isGroupScoped(await readMyAccess(context.queryClient)))
      throw redirect({
        to: "/groups",
        search: { ...search, view: undefined, group: undefined },
        replace: true,
      });
  },
  // The shell draws the permission state for a role or account the matrix's read refuses (J-209).
  staticData: { screenName: "Performance", requires: { acoQuality: ["read"] }, ownScope: true },
  component: PerformancePage,
});

function PerformancePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canReport = usePermission({ acoGaps: ["read"] });
  const access = useQuery(meAccessQueryOptions());
  const groupScoped = isGroupScoped(access.data);
  const granted = access.data?.groups ?? [];
  const canSeeGroups = usePermission({ acoQuality: ["read"] }) || groupScoped;
  const metadata = useQuery(metadataQueryOptions());
  const ui = useQuery(uiConfigQueryOptions());
  const document = uiDocumentSchema.safeParse(ui.data?.document);
  const status = shellStatus(metadata, { data: ui.data, error: ui.error, valid: document.success });
  if (status !== "ready" || metadata.data === undefined || !document.success)
    return (
      <ShellStatusMessage
        status={status === "ready" ? "loading" : status}
        onRetry={() => void Promise.all([metadata.refetch(), ui.refetch()])}
      />
    );
  const all = search.aco === undefined || search.aco === ALL_ACOS;
  if (!all && !metadata.data.acos.some((a) => a.acoCode === search.aco)) {
    const fallback = defaultAcoCode(metadata.data.acos);
    return (
      <AcoNotFound
        acoCode={search.aco ?? ""}
        fallback={fallback}
        renderLink={(code) => (
          <Link
            to="/groups"
            search={{ year: search.year, aco: code ?? ALL_ACOS }}
            className="underline"
          >
            {code === undefined ? "Show every ACO" : `Open ${code}`}
          </Link>
        )}
      />
    );
  }
  const year = resolveYear(metadata.data, search.year, search.aco);
  const acoCodes =
    year === undefined
      ? []
      : all
        ? acosWithData(metadata.data.acos, year).filter(
            (code) => !groupScoped || grantedAcoCodes(granted).includes(code),
          )
        : [search.aco!];
  if (year === undefined || acoCodes.length === 0)
    return (
      <p className="text-muted-foreground text-sm" data-testid="performance-no-results">
        No results for {year ?? "any year"}: no ACO in your access has results for that year.
      </p>
    );
  const { text, screens } = document.data;
  const byGroup = groupScoped || (canSeeGroups && requestedView(search) === "groups");
  const sort = parseSort(search.sort);
  const filter = parseFilter(search.filter);
  const onSortChange = (s: MatrixSort | undefined) =>
    void navigate({
      search: {
        ...search,
        sort:
          s === undefined
            ? undefined
            : `${s.measureKey}:${s.direction === "ascending" ? "asc" : "desc"}`,
      },
    });
  const onFilterChange = (f: MatrixFilter | undefined) =>
    void navigate({
      search: {
        ...search,
        filter: f === undefined ? undefined : `${f.measureKey ?? "any"}:${f.condition}:${f.value}`,
      },
    });
  return (
    <div className="grid gap-4">
      {canSeeGroups && !groupScoped ? (
        <ViewSwitch
          view={byGroup ? "groups" : "practices"}
          onViewChange={(view) =>
            void navigate({
              // A measure filter or sort carries over: both views share the measures.
              search: { ...search, view: viewKey(view) },
            })
          }
        />
      ) : null}
      {byGroup ? (
        <GroupPerformanceScreen
          // A new filter bar per view, so its fields always match the URL.
          key="groups"
          year={year}
          headerAco={all ? null : search.aco!}
          acoCodes={acoCodes}
          tabs={screens.quality_report.tabs}
          hiddenMeasures={screens.groups.hidden_measures}
          lowerIsBetter={text.lower_is_better}
          canReport={canReport}
          {...(groupScoped ? { grantedGroupIds: granted.map((g) => g.groupId) } : {})}
          sort={sort}
          filter={filter}
          onSortChange={onSortChange}
          onFilterChange={onFilterChange}
        />
      ) : (
        <PerformanceScreen
          key="practices"
          year={year}
          headerAco={all ? null : search.aco!}
          acoCodes={acoCodes}
          tabs={screens.quality_report.tabs}
          columns={screens.groups.columns}
          hiddenMeasures={screens.groups.hidden_measures}
          text={{
            performanceLabel: text.performance_label,
            totalRowLabel: text.total_row_label,
            lowerIsBetter: text.lower_is_better,
            dataLoaded: text.data_loaded,
          }}
          canReport={canReport}
          sort={sort}
          filter={filter}
          onSortChange={onSortChange}
          onFilterChange={onFilterChange}
          {...(canSeeGroups
            ? {
                groupFilter: {
                  aco: all ? undefined : search.aco,
                  group: search.group,
                  onGroupChange: (group: number | undefined) =>
                    void navigate({ search: { ...search, group } }),
                },
              }
            : {})}
        />
      )}
    </div>
  );
}
